import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { ListCostCentersQuery } from './CostCenter.types';

import {
  activeOrInactiveCostCenter,
  createCostCenter,
  editCostCenter,
  getCostCenter,
  listCostCenters,
  removeCostCenter,
} from './CostCenter.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';

const QUERY_ID = 'cost-centers';

export function useCostCenters(params?: ListCostCentersQuery) {
  const query = useInfiniteQuery(
    [QUERY_ID, { ...params }],
    ({ pageParam }) => {
      const req: ListCostCentersQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listCostCenters(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useCostCenter(id?: number) {
  const query = useQuery(
    ['costCenter', id],
    () => (id ? getCostCenter(id) : undefined),
    { enabled: !!id },
  );

  useServiceError(query.error);

  return query;
}

export function useCostCenterById() {
  const result = useMutation((id: number) => getCostCenter(id));

  useServiceError(result.error);

  return result;
}

export function useCostCenterCreate() {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const mutation = useMutation(createCostCenter, {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: `costCenters.create.notification.success`,
        }),
        description: intl.formatMessage({
          id: `costCenters.create.notification.successDescription`,
        }),
      });
      queryClient.invalidateQueries(QUERY_ID);
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.replace(
              'Cost Center already exists',
              'Centro de custo já existe',
            ),
          ),
      });
    },
  });

  return mutation;
}

export function useCostCenterEdit() {
  const queryClient = useQueryClient();
  const intl = useIntl().formatMessage;

  const mutation = useMutation(editCostCenter, {
    onSuccess() {
      notification.success({
        message: intl({ id: 'costCenters.update.notification.success' }),
        description: intl({
          id: 'costCenters.update.notification.successDescription',
        }),
      });
      queryClient.invalidateQueries(QUERY_ID);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useCostCenterDelete() {
  const queryClient = useQueryClient();
  const intl = useIntl().formatMessage;

  const mutation = useMutation(removeCostCenter, {
    onSuccess() {
      notification.success({
        message: intl({
          id: 'costCenters.contacts.remove.notification.success',
        }),
        description: intl({
          id: 'costCenters.contacts.remove.notification.successDescription',
        }),
      });
      queryClient.invalidateQueries(QUERY_ID);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Cost Center cannot be deleted due to relation.')
              ? 'Não é possivel excluir o centro de custo, pois ela tem vínculos com outros módulos.'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useActiveOrInactiveCostCenter() {
  const intl = useIntl().formatMessage;

  const mutation = useMutation(
    (costCenterId: number) => activeOrInactiveCostCenter(costCenterId),
    {
      onSuccess() {
        message.success(
          intl({ id: 'costCenters.update.status.message.success' }),
        );
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
